import React, { useState, useEffect } from "react";
import { db, storage } from "../../services/firebaseConfig";
import { collection, onSnapshot, doc, updateDoc, getDoc, deleteDoc, addDoc } from "firebase/firestore";
import { ref as storageRef, deleteObject, uploadBytes, getDownloadURL } from "firebase/storage";
import "../../styles/StockList.css";


const sizes = ["XS", "S", "M", "L", "XL", "2XL", "3XL"];
const years = ["4 tahun", "5 tahun", "6 tahun"];

const getStoragePathFromUrl = (imageUrl) => {
    if (!imageUrl) return null;

    try {
        const encodedPath = imageUrl.split("/o/")[1]?.split("?")[0];
        return encodedPath ? decodeURIComponent(encodedPath) : null;
    } catch (error) {
        console.warn("Gagal membaca laluan gambar lama:", error);
        return null;
    }
};


const StockList = () => {
    const [stocks, setStocks] = useState([]);
    const [editStockId, setEditStockId] = useState(null);
    const [quantities, setQuantities] = useState({});
    const currentUser = JSON.parse(localStorage.getItem("user"));
    const [editName, setEditName] = useState('');
    const [editImage, setEditImage] = useState(null);
    const [editDesc, setEditDesc] = useState(null);
    const [hapusModal, setHapusModal] = useState({ open: false, stock: null, kuantiti: 0, sebab: "" });

    const [previewImage, setPreviewImage] = useState('');
    const [editPrice, setEditPrice] = useState({});



    // ✅ Ambil data stok secara real-time
    useEffect(() => {
        const stocksCollection = collection(db, "inventori", "selangor", "stocks");
        let patched = false;

        const unsubscribe = onSnapshot(stocksCollection, async (snapshot) => {
            let stocksList = snapshot.docs.map((doc) => ({
                id: doc.id,
                ...doc.data(),
            }));

            // PATCH sekali je: kalau belum ada priority, set ikut turutan.
            if (!patched && stocksList.some(s => typeof s.priority !== "number")) {
                console.log("Patching priorities...");
                for (let i = 0; i < stocksList.length; i++) {
                    if (typeof stocksList[i].priority !== "number") {
                        console.log(`Set priority ${i} untuk ${stocksList[i].name} (${stocksList[i].id})`);
                        await updateDoc(doc(db, "inventori", "selangor", "stocks", stocksList[i].id), { priority: i });
                        stocksList[i].priority = i;
                    }
                }
                patched = true;
            }

            setStocks(stocksList);
        });

        return () => unsubscribe();
    }, []);

    const handleToggleInput = (stockId, stock = null) => {
        setEditStockId(editStockId === stockId ? null : stockId);
        setQuantities({});
        if (stock) {
            setEditName(stock.name || '');
            setEditDesc(stock.desc || '');
            setPreviewImage(stock.imageUrl || '');
            setEditImage(null);
        }
    };


    const handleQuantityChange = (key, value) => {
        setQuantities((prev) => ({
            ...prev,
            [key]: parseInt(value) || 0,
        }));
    };

    const handleAddStock = async (stock, isEdit = false) => {
        if (!editStockId || !stock) return;

        const stockRef = doc(db, "inventori", "selangor", "stocks", stock.id);
        const stockSnap = await getDoc(stockRef);
        if (!stockSnap.exists()) return;

        const stockData = stockSnap.data();
        let updatedFields = {};
        let hasValidQuantity = false;
        let newImagePath = null;

        // KEMASKINI NAMA
        if (editName && editName !== stock.name) {
            updatedFields.name = editName;
        }

        // KEMASKINI KETERANGAN (desc)
        if (typeof editDesc === 'string' && editDesc !== stock.desc) {
            updatedFields.desc = editDesc;
        }


        // KEMASKINI GAMBAR
        if (editImage) {
            const ext = editImage.name.split('.').pop();
            const path = `inventori/selangor/stocks/${stock.id}.${ext}`;
            const imageRef = storageRef(storage, path);
            await uploadBytes(imageRef, editImage);
            const newUrl = await getDownloadURL(imageRef);
            updatedFields.imageUrl = newUrl;
            newImagePath = path;
        }

        updatedFields.lastUpdatedBy = currentUser?.name || "anonymous";
        updatedFields.lastUpdatedById = currentUser?.id || "";
        updatedFields.lastUpdatedAt = new Date();

        // Tambah stok ikut kategori
        if (stock.category === "baju") {
            updatedFields.sizeQuantities = { ...stockData.sizeQuantities };
            Object.entries(quantities).forEach(([size, qty]) => {
                if (qty > 0) {
                    updatedFields.sizeQuantities[size] = (stockData.sizeQuantities[size] || 0) + qty;
                    hasValidQuantity = true;
                }
            });
        } else if (stock.category === "buku" && stock.yearQuantities) {
            updatedFields.yearQuantities = { ...stockData.yearQuantities };
            Object.entries(quantities).forEach(([year, qty]) => {
                if (qty > 0) {
                    updatedFields.yearQuantities[year] = (stockData.yearQuantities[year] || 0) + qty;
                    hasValidQuantity = true;
                }
            });
        } else if (stock.category === "buku") {
            let qtyToAdd = quantities["quantity"] || 0;
            if (qtyToAdd > 0) {
                updatedFields.quantity = (stockData.quantity || 0) + qtyToAdd;
                hasValidQuantity = true;
            }
        }
        else if (stock.category === "runcit") {
            let qtyToAdd = quantities["quantity"] || 0;
            if (qtyToAdd > 0) {
                updatedFields.quantity = (stockData.quantity || 0) + qtyToAdd;
                hasValidQuantity = true;
            }
        }

        // Jika takde update stok, pastikan tetap boleh update nama/gambar!
        if (!hasValidQuantity && !editName && !editImage) {
            alert("Sila masukkan kuantiti yang sah ATAU tukar nama/gambar.");
            return;
        }

        // =============================
        // PATCH: Update harga jika ada perubahan
        // =============================
        if (stock.category === "baju") {
            updatedFields.harga = { ...stockData.harga };
            sizes.forEach(size => {
                if (editPrice?.[size]) {
                    updatedFields.harga[size] = {
                        ...updatedFields.harga[size],
                        kos: Number(editPrice[size].kos ?? updatedFields.harga[size]?.kos ?? 0),
                        pengerusi: Number(editPrice[size].pengerusi ?? updatedFields.harga[size]?.pengerusi ?? 0),
                        pengurus: Number(editPrice[size].pengurus ?? updatedFields.harga[size]?.pengurus ?? 0)
                    }
                }
            });
        } else if (stock.category === "buku" && stock.yearQuantities) {
            updatedFields.harga = { ...stockData.harga };
            years.forEach(year => {
                if (editPrice?.[year]) {
                    updatedFields.harga[year] = {
                        ...updatedFields.harga[year],
                        kos: Number(editPrice[year].kos ?? updatedFields.harga[year]?.kos ?? 0),
                        pengerusi: Number(editPrice[year].pengerusi ?? updatedFields.harga[year]?.pengerusi ?? 0),
                        pengurus: Number(editPrice[year].pengurus ?? updatedFields.harga[year]?.pengurus ?? 0)
                    }
                }
            });
        } else if (stock.category === "buku") {
            updatedFields.harga = {
                kos: Number(editPrice.kos ?? stockData.harga?.kos ?? 0),
                pengerusi: Number(editPrice.pengerusi ?? stockData.harga?.pengerusi ?? 0),
                pengurus: Number(editPrice.pengurus ?? stockData.harga?.pengurus ?? 0)
            };
        } else if (stock.category === "runcit") {
            updatedFields.harga = {
                kos: Number(editPrice.kos ?? stockData.harga?.kos ?? 0),
                pengerusi: Number(editPrice.pengerusi ?? stockData.harga?.pengerusi ?? 0),
                pengurus: Number(editPrice.pengurus ?? stockData.harga?.pengurus ?? 0)
            };
        }


        await updateDoc(stockRef, updatedFields);

        // Gambar lama hanya dipadam selepas gambar baharu dan rekod Firestore
        // berjaya disimpan. Laluan sama bermakna upload sudah menimpa fail lama.
        if (editImage && stockData.imageUrl) {
            const oldImagePath = getStoragePathFromUrl(stockData.imageUrl);
            if (oldImagePath && oldImagePath !== newImagePath) {
                try {
                    await deleteObject(storageRef(storage, oldImagePath));
                } catch (error) {
                    console.warn("Gambar baharu disimpan, tetapi gambar lama gagal dipadam:", error);
                }
            }
        }

        setQuantities({});
        setEditStockId(null);
        setEditName('');
        setEditDesc('');
        setEditImage(null);
        setPreviewImage('');
        setEditPrice({});
        alert("Stok berjaya dikemaskini!");
    };


    const handleShowHapusModal = (stock) => {
        setHapusModal({ open: true, stock, kuantiti: 0, sebab: "", saiz: "", tahun: "" });
    };


    const handleHapusStok = async () => {
        const { stock, kuantiti, sebab, saiz, tahun } = hapusModal;
        if (!stock || !kuantiti || kuantiti <= 0 || !sebab) {
            alert("Sila isi kuantiti dan sebab.");
            return;
        }

        const stockRef = doc(db, "inventori", "selangor", "stocks", stock.id);
        const stockSnap = await getDoc(stockRef);
        if (!stockSnap.exists()) return;
        const stockData = stockSnap.data();

        let updatedFields = {};

        if (stock.category === "baju") {
            if (!saiz || !stockData.sizeQuantities?.[saiz]) return alert("Sila pilih saiz.");
            if (stockData.sizeQuantities[saiz] < kuantiti) return alert("Stok tak cukup.");
            updatedFields.sizeQuantities = { ...stockData.sizeQuantities, [saiz]: stockData.sizeQuantities[saiz] - kuantiti };
        } else if (stock.category === "buku") {
            const isSplitByYear =
                stockData.yearQuantities &&
                Object.keys(stockData.yearQuantities).length > 0;

            if (isSplitByYear) {
                if (!tahun) return alert("Sila pilih tahun.");
                const current = stockData.yearQuantities?.[tahun] ?? 0;
                if (current < kuantiti) return alert("Stok tak cukup.");

                updatedFields.yearQuantities = {
                    ...stockData.yearQuantities,
                    [tahun]: current - kuantiti,
                };
            } else {
                const currentQty = stockData.quantity ?? 0;
                if (currentQty < kuantiti) return alert("Stok tak cukup.");

                updatedFields.quantity = currentQty - kuantiti;
            }
        } else {
            if (stockData.quantity < kuantiti) return alert("Stok tak cukup.");
            updatedFields.quantity = stockData.quantity - kuantiti;
        }

        updatedFields.lastUpdatedBy = currentUser?.name || "anonymous";
        updatedFields.lastUpdatedAt = new Date();


        // PATCH update harga
        if (stock.category === "baju") {
            updatedFields.harga = { ...stockData.harga };
            sizes.forEach(size => {
                if (editPrice?.[size]) {
                    updatedFields.harga[size] = {
                        ...updatedFields.harga[size],
                        kos: Number(editPrice[size].kos ?? updatedFields.harga[size]?.kos ?? 0),
                        pengerusi: Number(editPrice[size].pengerusi ?? updatedFields.harga[size]?.pengerusi ?? 0),
                        pengurus: Number(editPrice[size].pengurus ?? updatedFields.harga[size]?.pengurus ?? 0)
                    }
                }
            });
        } else if (stock.category === "buku" && stock.yearQuantities) {
            updatedFields.harga = { ...stockData.harga };
            years.forEach(year => {
                if (editPrice?.[year]) {
                    updatedFields.harga[year] = {
                        ...updatedFields.harga[year],
                        kos: Number(editPrice[year].kos ?? updatedFields.harga[year]?.kos ?? 0),
                        pengerusi: Number(editPrice[year].pengerusi ?? updatedFields.harga[year]?.pengerusi ?? 0),
                        pengurus: Number(editPrice[year].pengurus ?? updatedFields.harga[year]?.pengurus ?? 0)
                    }
                }
            });
        } else if (stock.category === "buku") {
            updatedFields.harga = {
                kos: Number(editPrice.kos ?? stockData.harga?.kos ?? 0),
                pengerusi: Number(editPrice.pengerusi ?? stockData.harga?.pengerusi ?? 0),
                pengurus: Number(editPrice.pengurus ?? stockData.harga?.pengurus ?? 0)
            };
        }
        else if (stock.category === "runcit") {
            updatedFields.harga = {
                kos: Number(editPrice.kos ?? stockData.harga?.kos ?? 0),
                pengerusi: Number(editPrice.pengerusi ?? stockData.harga?.pengerusi ?? 0),
                pengurus: Number(editPrice.pengurus ?? stockData.harga?.pengurus ?? 0)
            };
        }



        await updateDoc(stockRef, updatedFields);

        await addDoc(collection(db, "inventori", "selangor", "hapusLogs"), {
            stockId: stock.id,
            stockName: stock.name,
            category: stock.category,
            saiz: saiz || "",
            tahun: tahun || "",
            kuantiti,
            sebab,
            hapusBy: currentUser?.name || "anonymous",
            hapusById: currentUser?.id || "",
            hapusAt: new Date()
        });

        setHapusModal({ open: false, stock: null, kuantiti: 0, sebab: "" });
        alert("Stok telah dihapus dan direkod.");
    };



    const handleDeleteStock = async (stock) => {
        const confirmDelete = window.confirm(`Padam item "${stock.name}"?`);
        if (!confirmDelete) return;

        try {
            // 1. Padam dokumen Firestore
            const stockDocRef = doc(db, "inventori", "selangor", "stocks", stock.id);
            await deleteDoc(stockDocRef);

            // 2. Padam gambar dari Storage
            if (stock.imageUrl) {
                const imagePath = getStoragePathFromUrl(stock.imageUrl);
                if (imagePath) {
                    const imageRef = storageRef(storage, imagePath);
                    await deleteObject(imageRef);
                }
            }

            alert("Stok dan gambar berjaya dipadam.");
        } catch (err) {
            console.error("Gagal padam stok:", err);
            alert("Terdapat ralat semasa memadam.");
        }
    };
    const moveStockUp = async (stockId) => {
        // Dapatkan semula stocks ikut priority
        const sortedStocks = [...stocks].sort((a, b) => (a.priority ?? 9999) - (b.priority ?? 9999));
        const idx = sortedStocks.findIndex(stock => stock.id === stockId);
        if (idx <= 0) return;

        const stockA = sortedStocks[idx];
        const stockB = sortedStocks[idx - 1];
        const priorityA = stockA.priority ?? idx;
        const priorityB = stockB.priority ?? idx - 1;

        console.log(`Naikkan ${stockA.name} (${priorityA}) <--> ${stockB.name} (${priorityB})`);

        await updateDoc(doc(db, "inventori", "selangor", "stocks", stockA.id), { priority: priorityB });
        await updateDoc(doc(db, "inventori", "selangor", "stocks", stockB.id), { priority: priorityA });
    };

    const moveStockDown = async (stockId) => {
        const sortedStocks = [...stocks].sort((a, b) => (a.priority ?? 9999) - (b.priority ?? 9999));
        const idx = sortedStocks.findIndex(stock => stock.id === stockId);
        if (idx === -1 || idx >= sortedStocks.length - 1) return;

        const stockA = sortedStocks[idx];
        const stockB = sortedStocks[idx + 1];
        const priorityA = stockA.priority ?? idx;
        const priorityB = stockB.priority ?? idx + 1;

        console.log(`Turunkan ${stockA.name} (${priorityA}) <--> ${stockB.name} (${priorityB})`);

        await updateDoc(doc(db, "inventori", "selangor", "stocks", stockA.id), { priority: priorityB });
        await updateDoc(doc(db, "inventori", "selangor", "stocks", stockB.id), { priority: priorityA });
    };




    return (
        <div className="stock-list-container">

            <h2>Senarai Stok</h2>
            <div className="stock-list">
                {[...stocks]
                    .sort((a, b) => (a.priority ?? 9999) - (b.priority ?? 9999))
                    .map((stock, idx, arr) => (
                        <div key={stock.id} className="stock-item">

                            {/* Butang Naik/Turun */}
                            <div className="move-btns">
                                <button className="move-btn" onClick={() => moveStockUp(stock.id)} disabled={idx === 0}>▲</button>
                                <button className="move-btn" onClick={() => moveStockDown(stock.id)} disabled={idx === arr.length - 1}>▼</button>
                            </div>



                            <img src={stock.imageUrl} alt={stock.name} className="stock-image" />
                            <div className="stock-details">
                                <h4>{stock.name}</h4>
                                <p>Kategori: {stock.category}</p>
                                {stock.desc && (
                                    <p style={{ fontStyle: "italic", color: "#555", margin: "4px 0 8px 0" }}>
                                        {stock.desc}
                                    </p>
                                )}

                                {stock.category === "runcit" && <p>Stok: {stock.quantity || 0}</p>}
                                {stock.category === "baju" && (
                                    <p>Stok: {
                                        sizes.map((size) => `${size}: ${stock.sizeQuantities?.[size] ?? 0}`).join(", ")
                                    }</p>
                                )}
                                {stock.category === "buku" && (
                                    stock.yearQuantities
                                        ? <p>Stok: {years.map(year => `${year}: ${stock.yearQuantities?.[year] ?? 0}`).join(", ")}</p>
                                        : <p>Stok: {stock.quantity || 0}</p>
                                )}


                                {stock.category === "baju" && (
                                    <div className="harga-baju-table">
                                        <strong>Harga Setiap Saiz:</strong>
                                        {sizes.map((size) =>
                                            stock.harga?.[size] ? (
                                                <div key={size} style={{ fontSize: "13px", marginLeft: 8 }}>
                                                    <span>{size}:</span>
                                                    <span> Kos: RM{Number(stock.harga[size].kos || 0).toFixed(2)},</span>
                                                    <span> Pengerusi: RM{Number(stock.harga[size].pengerusi || 0).toFixed(2)},</span>
                                                    <span> Pengurus: RM{Number(stock.harga[size].pengurus || 0).toFixed(2)}</span>
                                                </div>
                                            ) : null
                                        )}
                                    </div>
                                )}
                                {stock.category === "buku" && (
                                    stock.yearQuantities ? (
                                        <div className="harga-buku-table">
                                            <strong>Harga Setiap Tahun:</strong>
                                            {years.map((year) =>
                                                stock.harga?.[year] ? (
                                                    <div key={year} style={{ fontSize: "13px", marginLeft: 8 }}>
                                                        <span>{year}:</span>
                                                        <span> Kos: RM{Number(stock.harga[year].kos || 0).toFixed(2)},</span>
                                                        <span> Pengerusi: RM{Number(stock.harga[year].pengerusi || 0).toFixed(2)},</span>
                                                        <span> Pengurus: RM{Number(stock.harga[year].pengurus || 0).toFixed(2)}</span>
                                                    </div>
                                                ) : null
                                            )}
                                        </div>
                                    ) : (
                                        <div className="harga-buku-table">
                                            <strong>Harga:</strong>
                                            <div style={{ fontSize: "13px", marginLeft: 8 }}>
                                                <span> Kos: RM{Number(stock.harga?.kos || 0).toFixed(2)},</span>
                                                <span> Pengerusi: RM{Number(stock.harga?.pengerusi || 0).toFixed(2)},</span>
                                                <span> Pengurus: RM{Number(stock.harga?.pengurus || 0).toFixed(2)}</span>
                                            </div>
                                        </div>
                                    )
                                )}

                                {stock.category === "runcit" && (
                                    <>
                                        <p>Harga Kos: RM{Number(stock.harga?.kos || 0).toFixed(2)}</p>
                                        <p>Harga Pengerusi: RM{Number(stock.harga?.pengerusi || 0).toFixed(2)}</p>
                                        <p>Harga Pengurus: RM{Number(stock.harga?.pengurus || 0).toFixed(2)}</p>
                                    </>
                                )}


                                {editStockId === stock.id ? (
                                    <div className="add-stock-inputs">
                                        <div className="edit-modal-wrap">
                                            <div className="edit-form-fields">
                                                <label>
                                                    Nama:
                                                    <input
                                                        type="text"
                                                        value={editName}
                                                        onChange={e => setEditName(e.target.value)}
                                                    />
                                                </label>
                                                <label>
                                                    Keterangan:
                                                    <textarea
                                                        rows={2}
                                                        value={editDesc}
                                                        onChange={e => setEditDesc(e.target.value)}
                                                    />
                                                </label>
                                                <label>
                                                    Gambar:
                                                    <input
                                                        type="file"
                                                        accept="image/*"
                                                        onChange={e => {
                                                            if (e.target.files[0]) {
                                                                setEditImage(e.target.files[0]);
                                                                setPreviewImage(URL.createObjectURL(e.target.files[0]));
                                                            }
                                                        }}
                                                    />
                                                </label>
                                            </div>
                                            <div className="edit-form-image">
                                                {previewImage && <img src={previewImage} alt="preview" />}
                                            </div>
                                        </div>

                                        {stock.category === "baju" && (
                                            <div className="saiz-table-wrap">
                                                <table className="baju-size-table">
                                                    <thead>
                                                        <tr>
                                                            <th>Saiz</th>
                                                            <th>Stok Semasa</th>
                                                            <th>Tambah</th>
                                                            <th>Kos (RM)</th>
                                                            <th>Pengerusi (RM)</th>
                                                            <th>Pengurus (RM)</th>
                                                        </tr>
                                                    </thead>
                                                    <tbody>
                                                        {sizes.map((size) => (
                                                            <tr key={size}>
                                                                <td style={{ fontWeight: 500 }}>{size}</td>
                                                                <td>{stock.sizeQuantities?.[size] ?? 0}</td>
                                                                <td>
                                                                    <input
                                                                        type="number"
                                                                        min="0"
                                                                        style={{ width: 60 }}
                                                                        value={quantities[size] || ""}
                                                                        onChange={e => handleQuantityChange(size, e.target.value)}
                                                                        placeholder="+"
                                                                    />
                                                                </td>
                                                                <td>
                                                                    <input
                                                                        type="number"
                                                                        step="0.01"
                                                                        style={{ width: 70 }}
                                                                        value={editPrice?.[size]?.kos ?? stock.harga?.[size]?.kos ?? ""}
                                                                        onChange={e =>
                                                                            setEditPrice((p) => ({
                                                                                ...p,
                                                                                [size]: { ...p?.[size], kos: e.target.value },
                                                                            }))
                                                                        }
                                                                        placeholder="Kos"
                                                                    />
                                                                </td>
                                                                <td>
                                                                    <input
                                                                        type="number"
                                                                        step="0.01"
                                                                        style={{ width: 70 }}
                                                                        value={editPrice?.[size]?.pengerusi ?? stock.harga?.[size]?.pengerusi ?? ""}
                                                                        onChange={e =>
                                                                            setEditPrice((p) => ({
                                                                                ...p,
                                                                                [size]: { ...p?.[size], pengerusi: e.target.value },
                                                                            }))
                                                                        }
                                                                        placeholder="Pengerusi"
                                                                    />
                                                                </td>
                                                                <td>
                                                                    <input
                                                                        type="number"
                                                                        step="0.01"
                                                                        style={{ width: 70 }}
                                                                        value={editPrice?.[size]?.pengurus ?? stock.harga?.[size]?.pengurus ?? ""}
                                                                        onChange={e =>
                                                                            setEditPrice((p) => ({
                                                                                ...p,
                                                                                [size]: { ...p?.[size], pengurus: e.target.value },
                                                                            }))
                                                                        }
                                                                        placeholder="Pengurus"
                                                                    />
                                                                </td>
                                                            </tr>
                                                        ))}
                                                    </tbody>
                                                </table>
                                            </div>
                                        )}




                                        {stock.category === "buku" && (
                                            stock.yearQuantities ? (
                                                years.map((year) => (
                                                    <div key={year} className="order-item" style={{ display: "flex", gap: 8, alignItems: "center" }}>
                                                        <span style={{ width: 65 }}>{year}</span>
                                                        <input
                                                            type="number"
                                                            min="0"
                                                            style={{ width: 70 }}
                                                            onChange={(e) => handleQuantityChange(year, e.target.value)}
                                                            placeholder={`Tambah ke stok ${year}`}
                                                        />
                                                        <input
                                                            type="number"
                                                            step="0.01"
                                                            placeholder="Kos"
                                                            style={{ width: 65 }}
                                                            value={editPrice?.[year]?.kos ?? stock.harga?.[year]?.kos ?? ""}
                                                            onChange={e => setEditPrice(p => ({
                                                                ...p,
                                                                [year]: { ...p?.[year], kos: e.target.value }
                                                            }))}
                                                        />
                                                        <input
                                                            type="number"
                                                            step="0.01"
                                                            placeholder="Pengerusi"
                                                            style={{ width: 75 }}
                                                            value={editPrice?.[year]?.pengerusi ?? stock.harga?.[year]?.pengerusi ?? ""}
                                                            onChange={e => setEditPrice(p => ({
                                                                ...p,
                                                                [year]: { ...p?.[year], pengerusi: e.target.value }
                                                            }))}
                                                        />
                                                        <input
                                                            type="number"
                                                            step="0.01"
                                                            placeholder="Pengurus"
                                                            style={{ width: 75 }}
                                                            value={editPrice?.[year]?.pengurus ?? stock.harga?.[year]?.pengurus ?? ""}
                                                            onChange={e => setEditPrice(p => ({
                                                                ...p,
                                                                [year]: { ...p?.[year], pengurus: e.target.value }
                                                            }))}
                                                        />
                                                    </div>
                                                ))
                                            ) : (
                                                <div className="order-item" style={{ display: "flex", gap: 8, alignItems: "center" }}>
                                                    <span style={{ width: 65 }}>Jumlah</span>
                                                    <input
                                                        type="number"
                                                        min="0"
                                                        style={{ width: 70 }}
                                                        onChange={(e) => handleQuantityChange("quantity", e.target.value)}
                                                        placeholder="Tambah ke stok"
                                                    />
                                                    <input
                                                        type="number"
                                                        step="0.01"
                                                        placeholder="Kos"
                                                        style={{ width: 65 }}
                                                        value={editPrice?.kos ?? stock.harga?.kos ?? ""}
                                                        onChange={e => setEditPrice(p => ({
                                                            ...p,
                                                            kos: e.target.value
                                                        }))}
                                                    />
                                                    <input
                                                        type="number"
                                                        step="0.01"
                                                        placeholder="Pengerusi"
                                                        style={{ width: 75 }}
                                                        value={editPrice?.pengerusi ?? stock.harga?.pengerusi ?? ""}
                                                        onChange={e => setEditPrice(p => ({
                                                            ...p,
                                                            pengerusi: e.target.value
                                                        }))}
                                                    />
                                                    <input
                                                        type="number"
                                                        step="0.01"
                                                        placeholder="Pengurus"
                                                        style={{ width: 75 }}
                                                        value={editPrice?.pengurus ?? stock.harga?.pengurus ?? ""}
                                                        onChange={e => setEditPrice(p => ({
                                                            ...p,
                                                            pengurus: e.target.value
                                                        }))}
                                                    />
                                                </div>
                                            )
                                        )}


                                        {stock.category === "runcit" && (
                                            <div className="order-item" style={{ display: "flex", gap: 8, alignItems: "center" }}>
                                                <span>Jumlah</span>
                                                <input
                                                    type="number"
                                                    min="0"
                                                    style={{ width: 70 }}
                                                    onChange={(e) => handleQuantityChange("quantity", e.target.value)}
                                                />
                                                <input
                                                    type="number"
                                                    step="0.01"
                                                    placeholder="Kos"
                                                    style={{ width: 65 }}
                                                    value={editPrice?.kos ?? stock.harga?.kos ?? ""}
                                                    onChange={e => setEditPrice(p => ({
                                                        ...p,
                                                        kos: e.target.value
                                                    }))}
                                                />
                                                <input
                                                    type="number"
                                                    step="0.01"
                                                    placeholder="Pengerusi"
                                                    style={{ width: 75 }}
                                                    value={editPrice?.pengerusi ?? stock.harga?.pengerusi ?? ""}
                                                    onChange={e => setEditPrice(p => ({
                                                        ...p,
                                                        pengerusi: e.target.value
                                                    }))}
                                                />
                                                <input
                                                    type="number"
                                                    step="0.01"
                                                    placeholder="Pengurus"
                                                    style={{ width: 75 }}
                                                    value={editPrice?.pengurus ?? stock.harga?.pengurus ?? ""}
                                                    onChange={e => setEditPrice(p => ({
                                                        ...p,
                                                        pengurus: e.target.value
                                                    }))}
                                                />
                                            </div>
                                        )}


                                        {/* Butang Simpan dan Batal */}
                                        <div className="edit-modal-actions">
                                            <button className="cancel-button" style={{ color: "red" }} onClick={() => handleShowHapusModal(stock)}> Hapus Stok </button>
                                            <div className="group-btn">
                                                <button className="cancel-button" onClick={() => setEditStockId(null)}>Batal</button>
                                                <button className="submit-button" onClick={() => handleAddStock(stock, true)}>
                                                    Simpan
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                ) : (
                                    <div>
                                        <button className="submit-button" onClick={() => handleToggleInput(stock.id, stock)}>
                                            Edit Stok
                                        </button>


                                        <button className="delete-button" onClick={() => handleDeleteStock(stock)}>
                                            Padam
                                        </button>
                                    </div>
                                )}



                                <p style={{ fontSize: "12px", marginTop: "20px", color: "lightgray" }}>
                                    Dimasukkan oleh: {stock.createBy} pada:{" "}
                                    {stock.addedDate?.toDate().toLocaleString("ms-MY", {
                                        dateStyle: "medium",
                                        timeStyle: "short"
                                    })}
                                </p>
                                {stock.lastUpdatedBy && (
                                    <p style={{ fontSize: "12px", color: "gray" }}>
                                        Dikemaskini oleh: {stock.lastUpdatedBy} pada:{" "}
                                        {stock.lastUpdatedAt?.toDate().toLocaleString("ms-MY", {
                                            dateStyle: "medium",
                                            timeStyle: "short"
                                        })}
                                    </p>
                                )}
                            </div>
                        </div>
                    ))}
            </div>
            {hapusModal.open && hapusModal.stock && (
                <div className="modal-hapus-overlay">
                    <div className="modal-hapus-box">
                        <h4>Hapus Stok: {hapusModal.stock.name}</h4>
                        {/* Pilih saiz jika baju */}
                        {hapusModal.stock.category === "baju" && (
                            <select
                                value={hapusModal.saiz || ""}
                                onChange={e => setHapusModal(modal => ({ ...modal, saiz: e.target.value }))}
                            >
                                <option value="">Pilih saiz</option>
                                {sizes.map(sz => (
                                    <option key={sz} value={sz}>{sz} (ada: {hapusModal.stock.sizeQuantities?.[sz] ?? 0})</option>
                                ))}
                            </select>
                        )}
                        {/* Pilih tahun hanya jika buku ada pecahan tahun */}
                        {hapusModal.stock.category === "buku" && hapusModal.stock.yearQuantities && (
                            <select
                                value={hapusModal.tahun || ""}
                                onChange={e => setHapusModal(modal => ({ ...modal, tahun: e.target.value }))}
                            >
                                <option value="">Pilih tahun</option>
                                {years.map(yr => (
                                    <option key={yr} value={yr}>
                                        {yr} (ada: {hapusModal.stock.yearQuantities?.[yr] ?? 0})
                                    </option>
                                ))}
                            </select>
                        )}

                        <input
                            type="number"
                            min={1}
                            placeholder="Kuantiti"
                            value={hapusModal.kuantiti}
                            onChange={e => setHapusModal(modal => ({ ...modal, kuantiti: parseInt(e.target.value) }))}
                        />
                        <input
                            type="text"
                            placeholder="Sebab penghapusan"
                            value={hapusModal.sebab}
                            onChange={e => setHapusModal(modal => ({ ...modal, sebab: e.target.value }))}
                        />
                        <button onClick={handleHapusStok}>Hapus</button>
                        <button onClick={() => setHapusModal({ open: false, stock: null, kuantiti: 0, sebab: "" })}>Batal</button>
                    </div>
                </div>
            )}
        </div>
    );
};

export default StockList;
